const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

let accessToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

// AuthProvider registers this so the client can force a logout when the
// refresh token itself is invalid/expired (no infinite retry loops).
export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler;
}

let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_URL}/api/auth/refresh`, { method: "POST", credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return false;
        const data = await res.json();
        setAccessToken(data.accessToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  isRetry?: boolean;
  headers?: Record<string, string>;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers: {
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      "X-App-Language": appLanguage(),
      ...(opts.headers ?? {})
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined
  });

  if (res.status === 401 && !opts.isRetry && !path.startsWith("/api/auth/")) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return request<T>(path, { ...opts, isRetry: true });
    unauthorizedHandler?.();
    throw new ApiError(401, "unauthorized", "Session expired");
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    announceStorageFull(res.status, data);
    throw new ApiError(res.status, data.error ?? "error", data.message ?? "Request failed");
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  postWithHeaders: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>(path, { method: "POST", body, headers }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" })
};

// Multipart upload - separate from `request` because it must not set a JSON Content-Type.
export async function apiUpload<T = { url: string }>(path: string, file: File | Blob, filename = "file"): Promise<T> {
  const form = new FormData();
  form.append("file", file, filename);

  const doUpload = async (isRetry: boolean): Promise<T> => {
    const res = await fetch(`${API_URL}${path}`, {
      method: "POST",
      credentials: "include",
      // apiUpload language: the server words any error in the language the app shows
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}), "X-App-Language": appLanguage() },
      body: form
    });

    if (res.status === 401 && !isRetry) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return doUpload(true);
      unauthorizedHandler?.();
      throw new ApiError(401, "unauthorized", "Session expired");
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      announceStorageFull(res.status, data);
      throw new ApiError(res.status, data.error ?? "error", data.message ?? "Upload failed");
    }
    return data;
  };

  return doUpload(false);
}

export function mediaUrl(path: string | null | undefined) {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  return `${API_URL}${path}`;
}

/** The language the app is showing (what the person picked, else the browser's). The server words weather notes and channel text in it. */
export function appLanguage(): string {
  try {
    const v = localStorage.getItem("kaventine_language");
    if (v && /^[A-Za-z-]{2,8}$/.test(v)) return v;
  } catch { /* private mode */ }
  const n = typeof navigator !== "undefined" && navigator.language ? navigator.language : "en";
  return /^[A-Za-z-]{2,8}$/.test(n) ? n : "en";
}

/** An upload the server refused because the person's storage is full (413 storage_full): tells the UI once, which shows a toast with a way forward. */
function announceStorageFull(status: number, data: { error?: string; plus?: boolean; trialAvailable?: boolean }) {
  if (status === 413 && data?.error === "storage_full" && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("sk:storage-full", { detail: { plus: !!data.plus, trialAvailable: !!data.trialAvailable } }));
  }
}

export { API_URL };
